import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FaSave, FaTimes } from "react-icons/fa";

import {
  buildInitialFormFromGroup,
  COLOR_OPTIONS,
  normalizeGroupPayload,
  validateGroupForm,
} from "../utils/groupesUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-orange-500/10";

const labelClass =
  "mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200";

function TextInput({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
  inputMode,
}) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>

      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        inputMode={inputMode}
        className={inputClass}
      />
    </div>
  );
}

export default function GroupForm({
  mode = "create",
  group,
  saving = false,
  onSubmit,
  onClose,
}) {
  const initialForm = useMemo(() => buildInitialFormFromGroup(group), [group]);

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    setFormData(initialForm);
  }, [initialForm]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = normalizeGroupPayload(formData);
    const errorMessage = validateGroupForm(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    await onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Identité du groupe
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Nom du groupe"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            required
            placeholder="Ex : Poussins, Adultes débutants..."
          />

          <TextInput
            label="Années de naissance"
            name="annees_naissance"
            value={formData.annees_naissance}
            onChange={handleChange}
            placeholder="Ex : 2014-2016"
          />

          <div className="sm:col-span-2">
            <label className={labelClass}>Couleur du groupe</label>

            <div className="grid gap-2 sm:grid-cols-3">
              {COLOR_OPTIONS.map((color) => (
                <label
                  key={color.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-bold transition ${
                    formData.color_groupe === color.value
                      ? "border-orange-300 bg-orange-50 text-orange-700 ring-4 ring-orange-100 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/10"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="color_groupe"
                    value={color.value}
                    checked={formData.color_groupe === color.value}
                    onChange={handleChange}
                    className="sr-only"
                  />

                  <span
                    className={`h-5 w-5 rounded-full border border-slate-200 ${color.value}`}
                  />
                  {color.label}
                </label>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Horaires
        </h3>

        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Format conseillé : “Mardi 17h00-18h00” ou “Samedi 10h00-11h30”.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Horaire 1"
            name="horaire1"
            value={formData.horaire1}
            onChange={handleChange}
            placeholder="Mardi 17h00-18h00"
          />

          <TextInput
            label="Horaire 2"
            name="horaire2"
            value={formData.horaire2}
            onChange={handleChange}
            placeholder="Jeudi 18h00-19h00"
          />
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Tarifs & capacité
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Tarif licence"
            name="tarif_licence"
            type="number"
            value={formData.tarif_licence}
            onChange={handleChange}
            placeholder="Ex : 41"
            inputMode="decimal"
          />

          <TextInput
            label="Tarif cotisation"
            name="tarif_cotisation"
            type="number"
            value={formData.tarif_cotisation}
            onChange={handleChange}
            placeholder="Ex : 120"
            inputMode="decimal"
          />

          <TextInput
            label="Quota du groupe"
            name="quota_groupe"
            type="number"
            value={formData.quota_groupe}
            onChange={handleChange}
            placeholder="Ex : 30"
            inputMode="numeric"
          />

          <TextInput
            label="Ordre d'affichage"
            name="ordre_groupe"
            type="number"
            value={formData.ordre_groupe}
            onChange={handleChange}
            placeholder="Ex : 1"
            inputMode="numeric"
          />
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Encadrement
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Enseignant principal"
            name="enseignant1"
            value={formData.enseignant1}
            onChange={handleChange}
            required
            placeholder="Nom de l'enseignant"
          />

          <TextInput
            label="Enseignant secondaire"
            name="enseignant2"
            value={formData.enseignant2}
            onChange={handleChange}
            placeholder="Optionnel"
          />
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-900">
          <div>
            <p className="font-bold text-slate-900 dark:text-white">
              Groupe actif
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Un groupe actif peut être affiché et utilisé pour les inscriptions.
            </p>
          </div>

          <input
            type="checkbox"
            name="is_active"
            checked={Boolean(formData.is_active)}
            onChange={handleChange}
            className="h-5 w-5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
          />
        </label>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaSave />
            {saving
              ? "Enregistrement..."
              : mode === "create"
              ? "Créer le groupe"
              : "Enregistrer"}
          </button>
        </div>
      </div>
    </form>
  );
}